// Global Instructions Rule Applied!
import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getAccessToken } from '../../../auth/authService'
import { store } from '../../../../state_manager/store.ts'
import {
  setActiveRecord,
  registerRecordUsage,
  unregisterRecordUsage,
  selectActiveRecordById
} from '../../../../state_manager/state_update_actions/active_records/index.ts'

export interface DetailFetchResult<T> {
  data: T | null
  error?: string
}

export type ModuleSelector<T> = (state: any, id: string) => T | null | undefined
export type DetailFetcher<T> = (id: string, accessToken: string | null) => Promise<DetailFetchResult<T>>

/**
 * Slice-first-then-fetch read for a mobile detail screen. Mirrors the desktop
 * Full-form lifecycle (see app_modules/*\/components/forms/Full.tsx and
 * funds/hooks/useFundDashboard): render whatever's already cached in activeRecords
 * or the module slice, and on a cold load (deep link / hard refresh, where both
 * slices are empty) fetch the detail RPC, cache it via setActiveRecord, and pin it
 * with registerRecordUsage('fullForm') so the activeRecords reducer doesn't evict it
 * while the screen is open.
 *
 * `selectFromModule` and `fetchById` MUST be stable references (module-level
 * imports), since they are effect dependencies.
 */
export function useRecordDetail<T = Record<string, any>>(
  recordId: string | undefined | null,
  selectFromModule: ModuleSelector<T>,
  fetchById: DetailFetcher<T>
): { record: T | null; loading: boolean; error: string | null } {
  const dispatch = useDispatch()
  const id = recordId ? String(recordId) : null

  const fromActiveRecords = useSelector((state: any) => (id ? selectActiveRecordById(state, id) : null))
  const fromModule = useSelector((state: any) => (id ? selectFromModule(state, id) : null))
  const record = ((fromActiveRecords as T) ?? (fromModule as T) ?? null) as T | null

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const hasAttemptedFetch = useRef(false)
  const didRegister = useRef(false)

  // Reset the fetch guard whenever the record id changes.
  useEffect(() => {
    hasAttemptedFetch.current = false
  }, [id])

  useEffect(() => {
    if (!id) {
      setLoading(false)
      setError(null)
      return
    }
    if (hasAttemptedFetch.current) return

    // Already cached (slice prefill from a list page, or a prior fetch) — no network.
    const cached = selectActiveRecordById(store.getState(), id) ?? selectFromModule(store.getState(), id)
    if (cached) return

    hasAttemptedFetch.current = true
    let cancelled = false

    const run = async () => {
      setLoading(true)
      setError(null)
      try {
        const token = getAccessToken(store.getState().auth)
        const res = await fetchById(id, token)
        if (cancelled) return
        if (res.error) setError(res.error)
        if (res.data) {
          dispatch(setActiveRecord({ uuid: id, record: res.data }))
          dispatch(registerRecordUsage({ uuid: id, context: 'fullForm' }))
          didRegister.current = true
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load record')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    run()
    return () => {
      cancelled = true
    }
  }, [id, dispatch, fetchById, selectFromModule])

  // Release the activeRecords pin on unmount / id change (only if we set it).
  useEffect(() => {
    return () => {
      if (id && didRegister.current) {
        dispatch(unregisterRecordUsage({ uuid: id, context: 'fullForm' }))
        didRegister.current = false
      }
    }
  }, [id, dispatch])

  return { record, loading, error }
}
